"use client";

import type { Event } from "../types";

interface MarketsListChangeCellsProps {
  /** Either a real Event or a sub-market wrapped as `{ markets: [m] }` for nested rows. */
  event: Event | { markets: any[] };
  /**
   * Caller-supplied price-change accessor (parent already imports
   * getEventPriceChange from helpers/price-change). Passing it in
   * keeps this component free of source-specific lookup quirks.
   */
  getEventPriceChange: (event: any, timeframe: "1h" | "24h" | "7d") => number | null;
  /** Cell padding — main rows use `py-2.5`, nested rows use `py-3`. Default main. */
  variant?: "main" | "nested";
}

/**
 * Per-timeframe % change cells (1h / 24h / 7d) for the markets
 * list-view row. Polymarket only — Kalshi and Predict slim payloads
 * don't ship the deltas, so the parent should gate this with
 * `effectiveSourceFilter === 'polymarket'` to avoid empty columns.
 *
 * Returns a Fragment of three `<td>`s so it slots into the parent
 * `<tr>` next to the Price cell. "N/A" is rendered when data is
 * missing rather than 0% (which would be misleading).
 *
 * The helper accessor is passed in because consumers want a
 * source-aware lookup that consults `event.yesHistory` AND
 * `markets[0].yesHistory` — events that only carry history at the
 * sub-market level (some multi-outcome futures) still need to render.
 */
export function MarketsListChangeCells({ event, getEventPriceChange, variant = "main" }: MarketsListChangeCellsProps) {
  const yClass = variant === "nested" ? "py-3" : "py-2.5";
  return (
    <>
      {(["1h", "24h", "7d"] as const).map((tf) => {
        const raw = getEventPriceChange(event, tf);
        const pct = raw == null ? null : raw * 100;
        return (
          <td key={tf} className={`px-2 ${yClass} text-right tabular-nums`}>
            {pct == null ? (
              <span className="text-[11px] font-medium tabular-nums text-gray-400 dark:text-gray-600">N/A</span>
            ) : (
              <span
                className={`inline-flex items-center justify-end gap-0.5 text-[11px] font-medium ${
                  pct > 0
                    ? "text-emerald-500 dark:text-emerald-400"
                    : pct < 0
                      ? "text-red-500 dark:text-red-400"
                      : "text-gray-400"
                }`}
              >
                <span className="text-[8px] leading-none">
                  {pct > 0 ? "▲" : pct < 0 ? "▼" : "·"}
                </span>
                {Math.abs(pct).toFixed(2)}%
              </span>
            )}
          </td>
        );
      })}
    </>
  );
}
